import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Avatar,
  Chip,
  Grid,
  Link as MuiLink,
  Paper,
  useTheme,
  Button,
  useMediaQuery,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import { getReviewDetail, getGameDetails } from '../services/api';
import { ReviewDetail } from '../types/Review';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import ShareButtons from '../components/common/ShareButtons';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { likeReview } from '../services/api';
import { Helmet } from 'react-helmet-async';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import { getPlatformDisplayText } from '../utils/PlatformList';
import { formatReviewText_v2 } from '../utils/formatReviewText_v2';
import DetailScores from '../components/review/DetailScores';
import { scoreTags } from '../utils/scoreTags';
import UpdateIcon from '@mui/icons-material/Update';


const getScoreColor = (score: number | null) => {
  if (score === null) return "#666666";
  if (score >= 4.5) return "#4CAF50";
  if (score >= 4.0) return "#8BC34A";
  if (score >= 3.5) return "#CDDC39";
  if (score >= 3.0) return "#FFEB3B";
  if (score >= 2.5) return "#FFC107";
  if (score >= 2.1) return "#FF9800";
  return "#F44336";
};

const getScoreLabel = (score: number | null) => {
  if (score === null) return "未評価";
  if (score >= 4.5) return "傑作";
  if (score >= 4.0) return "名作";
  if (score >= 3.5) return "良作";
  if (score >= 3.0) return "佳作";
  if (score >= 2.5) return "凡作";
  return "駄作";
};

const GameReviewDetailPage_v2: React.FC = () => {
  const { gameId, reviewId } = useParams<{ gameId: string; reviewId: string }>();
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [isLiked, setIsLiked] = useState(review?.is_liked || false);
  const [likeCount, setLikeCount] = useState(review?.like_count || 0);
  const [userLikeCount, setUserLikeCount] = useState(0);
  const isAuthenticated = !!localStorage.getItem('token');
  const navigate = useNavigate();
  const location = useLocation();
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [gameDetails, setGameDetails] = useState<any>(null);

  useEffect(() => {
    const fetchReviewDetail = async () => {
      try {
        const reviewData = await getReviewDetail(Number(gameId), Number(reviewId));
        setReview(reviewData);

        // ゲーム詳細情報も取得（Steam IDなど）
        const gameData = await getGameDetails(Number(gameId));
        setGameDetails(gameData);
      } catch (error) {
        console.error('レビュー詳細の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviewDetail();
  }, [gameId, reviewId]);

  // Steam画像URLを生成（SteamリンクがあればSteam、なければカバー画像）
  useEffect(() => {
    if (!gameDetails) return;

    if (gameDetails.links?.steam) {
      const appId = gameDetails.links.steam;
      // Steam library_hero画像URL
      const steamHeroUrl = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${appId}/library_hero.jpg`;

      // 画像が存在するか確認
      const img = new Image();
      img.onload = () => {
        setHeroImage(steamHeroUrl);
      };
      img.onerror = () => {
        // Steam画像が存在しない場合はカバー画像を使用
        setHeroImage(review?.game?.cover || null);
      };
      img.src = steamHeroUrl;
    } else {
      // Steamリンクがない場合はカバー画像を使用
      setHeroImage(review?.game?.cover || null);
    }
  }, [gameDetails, review]);

  useEffect(() => {
    if (review) {
      setIsLiked(review.is_liked);
      setLikeCount(review.like_count);
    }
  }, [review]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReviewExcerpt = (text: string, maxLength: number = 60) => {
    if (!text) return '';
    const trimmed = text.trim().replace(/\s+/g, ' ');
    return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}...` : trimmed;
  };

  const handleLikeClick = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    try {
      if (userLikeCount >= 10) {
        console.error('いいねの上限（10回）に達しています。');
        return;
      }
      const response = await likeReview(Number(reviewId));
      setIsLiked(true);
      setLikeCount(response.data.count);
      setUserLikeCount(response.data.like_count);
    } catch (error: any) {
      console.error('イイね処理に失敗しました', error);
      if (error?.response?.data?.error) {
        console.error(error.response.data.error);
      }
    }
  };

  if (loading) return <Typography>読み込み中...</Typography>;
  if (!review) return <Typography>レビューが見つかりません</Typography>;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Helmet>
        <title>
          {review ? `${getReviewExcerpt(review.review_text, 30)}｜${review.game.japanese_name || review.game.name}のレビュー${review.game.japanese_name && review.game.name ? `｜${review.game.name}` : ''}｜PULSE` : 'レビュー｜PULSE'}
        </title>
        <meta
          name="description"
          content={review?.review_text || 'レビューの読み込み中...'}
        />
      </Helmet>

      {/* ヒーローセクション - ゲーム背景 */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 300, sm: 400, md: 500 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${heroImage || review.game.cover || '/image/no_image.png'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            // Steam画像の場合はぼかしなし、カバー画像の場合はぼかしあり
            filter: heroImage && gameDetails?.links?.steam ? 'none' : 'blur(40px) brightness(0.6)',
            transform: heroImage && gameDetails?.links?.steam ? 'none' : 'scale(1.1)',
          }
        }}
      >
        {/* グラデーションオーバーレイ - 下に向かってフェードアウト */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, #121212 100%)',
          }}
        />

        {/* ヒーローコンテンツ */}
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end',
            pb: { xs: 4, md: 6 }
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', gap: 3, alignItems: 'flex-end' }}>
            {/* ジャケット画像 - Steam画像がない場合のみ表示 */}
            {!(heroImage && gameDetails?.links?.steam) && (
              <Box
                component={RouterLink}
                to={`/game/${gameId}`}
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  flexShrink: 0,
                  textDecoration: 'none',
                  '& img': {
                    width: { sm: 120, md: 150 },
                    height: 'auto',
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                    transition: 'transform 0.3s',
                  },
                  '&:hover img': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <img
                  src={review.game.cover || '/image/no_image.png'}
                  alt={review.game.name}
                />
              </Box>
            )}

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(218, 218, 218, 0.9)',
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  display: 'block',
                  mb: 1.25,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              >
                Game Review
              </Typography>
              <MuiLink
                component={RouterLink}
                to={`/game/${gameId}`}
                sx={{
                  textDecoration: 'none',
                  '&:hover h1': {
                    color: 'primary.light'
                  }
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    color: 'white',
                    fontSize: { xs: '1.25rem', sm: '2rem', md: '2rem' },
                    fontWeight: 600,
                    lineHeight: 1.2,
                    transition: 'color 0.3s',
                    textShadow: '0 4px 20px rgba(0,0,0,0.8)'
                  }}
                >
                  {review.game.japanese_name || review.game.name}
                </Typography>
              </MuiLink>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* メインコンテンツ */}
      <Container maxWidth="lg" sx={{ mt: { xs: -6, md: -8 }, position: 'relative', zIndex: 1, pb: 8 }}>
        <Grid container spacing={6}>
          {/* レビュー本文エリア */}
          <Grid item xs={12} md={8.5}>
            {/* スコアカード - モバイルのみ最上部表示 */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${getScoreColor(review.score)}20 0%, rgba(0,0,0,0.6) 100%)`,
                  border: '2px solid',
                  borderColor: `${getScoreColor(review.score)}40`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  {/* 総合スコア */}
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: 1.5,
                          display: 'block',
                          mb: 1.5,
                          fontSize: '0.65rem',
                          fontWeight: 700
                        }}
                      >
                        Overall Score
                      </Typography>
                      {review.score !== null ? (
                        <Box>
                          <Typography
                            variant="h1"
                            sx={{
                              fontSize: { xs: '3rem', md: '3.5rem' },
                              fontWeight: 800,
                              color: getScoreColor(review.score),
                              lineHeight: 1,
                              mb: 1,
                              textShadow: `0 0 30px ${getScoreColor(review.score)}40`
                            }}
                          >
                            {review.score.toFixed(1)}
                          </Typography>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 1
                            }}
                          >
                            {[...Array(5)].map((_, i) => {
                              const score = review.score || 0;
                              const fullStars = Math.floor(score);
                              const hasHalfStar = (score % 1) >= 0.25 && (score % 1) < 0.75;

                              if (i < fullStars) {
                                // Full star
                                return <StarRoundedIcon key={i} sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, color: getScoreColor(review.score) }} />;
                              } else if (i === fullStars && hasHalfStar) {
                                // Half star
                                return <StarHalfRoundedIcon key={i} sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, color: getScoreColor(review.score) }} />;
                              } else {
                                // Empty star
                                return <StarRoundedIcon key={i} sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, color: 'rgba(255,255,255,0.15)' }} />;
                              }
                            })}
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: getScoreColor(review.score),
                              fontWeight: 600,
                              fontSize: '0.95rem'
                            }}
                          >
                            {getScoreLabel(review.score)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="h4" color="text.secondary">
                          未評価
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* 詳細スコア */}
                  <Grid item xs={12} sm={8}>
                    <DetailScores
                      scoreVisual={review.score_visual}
                      scoreSound={review.score_sound}
                      scoreGameplay={review.score_gameplay}
                      scoreNarrative={review.score_narrative}
                      scoreOriginality={review.score_originality}
                    />
                  </Grid>
                </Grid>

                {/* スコアタグ */}
                {review.score_tags && review.score_tags.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {review.score_tags.map((tagId: number) => {
                        const tag = scoreTags.find((t) => t.id === tagId);
                        return tag && (
                          <Chip
                            key={tag.id}
                            icon={
                              tag.id === 9 || tag.id >= 30 ? (
                                <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                              ) : (
                                <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                              )
                            }
                            label={tag.label}
                            size="medium"
                            variant="outlined"
                            sx={{
                              '& .MuiChip-label': {
                                fontSize: '0.875rem',
                                fontWeight: 500
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
            <Box>
              {/* レビュワー情報 */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={review.avatar || undefined}
                    alt={review.username}
                    sx={{
                      width: 48,
                      height: 48,
                      border: '2px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    {/* BY ライター名 部分 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontSize: { xs: '0.8rem', sm: '0.85rem' },
                          fontWeight: 400,
                        }}
                      >
                        review by
                      </Typography>
                      <MuiLink
                        component={RouterLink}
                        to={`/profile/${review.username}`}
                        sx={{
                          textDecoration: 'none',
                          color: 'text.primary',
                          fontWeight: 400,
                          fontSize: { xs: '0.8rem', sm: '0.85rem' },
                          '&:hover': {
                            color: 'primary.main',
                          }
                        }}
                      >
                        {review.display_name || review.username}
                      </MuiLink>
                      {review.status && review.status !== 1 && (
                        <Chip
                          label={review.status === 2 ? 'プレイ中' : review.status === 3 ? '中断' : ''}
                          size="small"
                          sx={{
                            bgcolor: review.status === 2 ? '#2196F330' : '#FF980030',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: `1.5px solid ${review.status === 2 ? '#2196F360' : '#FF980060'}`,
                            fontSize: '0.65rem',
                            height: 22,
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                    {/* 日付部分 - デスクトップは横並び、モバイルは改行 */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75
                    }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem'
                        }}
                      >
                        {formatDate(review.created_at)}に投稿
                      </Typography>
                      {review.updated_at && review.updated_at !== review.created_at && (
                        <Tooltip title={`更新日：${formatDate(review.updated_at)}`}>
                          <UpdateIcon sx={{
                            fontSize: '1rem',
                            color: 'text.secondary',
                            cursor: 'help'
                          }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* レビュー本文 */}
              <Box
                sx={{
                  mb: 6,
                  borderLeft: { xs: 'none', md: `4px solid ${getScoreColor(review.score)}40` },
                  pl: { xs: 0, md: 3 },
                  lineHeight: 1.9,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  color: 'text.primary',
                  letterSpacing: '0.02em',
                  fontWeight: 400,
                  '& .spoiler': {
                    backgroundColor: '#444',
                    padding: '0 4px',
                    borderRadius: '2px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  },
                  '& a:hover': {
                    opacity: 0.8
                  }
                }}
              >
                {formatReviewText_v2(review.review_text, isMobile, getScoreColor(review.score))}
              </Box>

              {/* アクションエリア */}
              <Box
                sx={{
                  pt: 4,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant={isLiked ? "contained" : "outlined"}
                    size="large"
                    onClick={handleLikeClick}
                    startIcon={isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                    disabled={userLikeCount >= 10}
                    sx={{
                      minWidth: 140,
                      fontWeight: 700,
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      fontSize: '1rem'
                    }}
                  >
                    {likeCount}
                    {userLikeCount > 0 && ` (+${userLikeCount})`}
                  </Button>
                  <ShareButtons
                    text={`${review.game.japanese_name || review.game.name}のレビュー by ${review.display_name || review.username}

${getReviewExcerpt(review.review_text)}`}
                    url={`/game/${gameId}/reviews/${reviewId}`}
                    hashtags={['PULSE_DB']}
                    buttonText='共有'
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* サイドバー - プレイ情報 */}
          <Grid item xs={12} md={3.5}>
            <Stack spacing={3} sx={{ position: 'sticky', top: 24 }}>
              {/* スコアカード - デスクトップのみ表示 */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${getScoreColor(review.score)}20 0%, rgba(0,0,0,0.6) 100%)`,
                    border: '2px solid',
                    borderColor: `${getScoreColor(review.score)}40`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  }}
                >
                  {/* 総合スコア */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 1.5,
                        display: 'block',
                        mb: 1.5,
                        fontSize: '0.65rem',
                        fontWeight: 700
                      }}
                    >
                      Overall Score
                    </Typography>
                    {review.score !== null ? (
                      <Box>
                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: '3.5rem',
                            fontWeight: 800,
                            color: getScoreColor(review.score),
                            lineHeight: 1,
                            mb: 1,
                            textShadow: `0 0 30px ${getScoreColor(review.score)}40`
                          }}
                        >
                          {review.score.toFixed(1)}
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mb: 1
                          }}
                        >
                          {[...Array(5)].map((_, i) => {
                            const score = review.score || 0;
                            const fullStars = Math.floor(score);
                            const hasHalfStar = (score % 1) >= 0.25 && (score % 1) < 0.75;

                            if (i < fullStars) {
                              // Full star
                              return <StarRoundedIcon key={i} sx={{ fontSize: '1.3rem', color: getScoreColor(review.score) }} />;
                            } else if (i === fullStars && hasHalfStar) {
                              // Half star
                              return <StarHalfRoundedIcon key={i} sx={{ fontSize: '1.3rem', color: getScoreColor(review.score) }} />;
                            } else {
                              // Empty star
                              return <StarRoundedIcon key={i} sx={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.15)' }} />;
                            }
                          })}
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: getScoreColor(review.score),
                            fontWeight: 600,
                            fontSize: '0.95rem'
                          }}
                        >
                          {getScoreLabel(review.score)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="h4" color="text.secondary">
                        未評価
                      </Typography>
                    )}
                  </Box>

                  {/* 詳細スコア */}
                  <DetailScores
                    scoreVisual={review.score_visual}
                    scoreSound={review.score_sound}
                    scoreGameplay={review.score_gameplay}
                    scoreNarrative={review.score_narrative}
                    scoreOriginality={review.score_originality}
                  />

                  {/* スコアタグ */}
                  {review.score_tags && review.score_tags.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {review.score_tags.map((tagId: number) => {
                          const tag = scoreTags.find((t) => t.id === tagId);
                          return tag && (
                            <Chip
                              key={tag.id}
                              icon={
                                tag.id === 9 || tag.id >= 30 ? (
                                  <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                                ) : (
                                  <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                                )
                              }
                              label={tag.label}
                              size="small"
                              variant="outlined"
                              sx={{
                                '& .MuiChip-label': {
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* プレイ情報カード */}
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'divider',
                  background: 'background.paper',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    fontSize: '1rem',
                    letterSpacing: 0.3
                  }}
                >
                  プレイ情報
                </Typography>
                <Stack spacing={3}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimerOutlinedIcon sx={{ fontSize: '1.2rem', mr: 1, color: 'primary.main' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                        プレイ時間
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ pl: 0, fontWeight: 600 }}>
                      {review.play_time ? `${review.play_time}時間` : '未記録'}
                    </Typography>
                  </Box>

                  <Divider sx={{ opacity: 0.5 }} />

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PlayCircleOutlineIcon sx={{ fontSize: '1.2rem', mr: 1, color: 'primary.main' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                        プレイ期間
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ pl: 0, fontWeight: 500 }}>
                      {review.play_start_date || '未記録'}<br />〜 {review.play_end_date || '未記録'}
                    </Typography>
                  </Box>

                  {review.platform && review.platform.length > 0 && (
                    <>
                      <Divider sx={{ opacity: 0.5 }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <SportsEsportsOutlinedIcon sx={{ fontSize: '1.2rem', mr: 1, color: 'primary.main' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            プラットフォーム
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ pl: 0, fontWeight: 500 }}>
                          {getPlatformDisplayText(review.platform)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              </Paper>

              {/* ゲーム詳細へのリンク */}
              <Button
                component={RouterLink}
                to={`/game/${gameId}`}
                variant="outlined"
                fullWidth
                size="large"
                sx={{
                  py: 1.75,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2
                  }
                }}
              >
                ゲーム詳細を見る
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GameReviewDetailPage_v2;
